import React from 'react';
import { Route, withRouter } from 'react-router-dom';
import { renderRoutes } from 'react-router-config';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import * as Error404Actions from 'rebook/apps/base/actions/Error404Actions';
import * as AppActions from 'rebook/apps/base/actions/AppActions';
import * as PlayerActions from 'rebook/apps/base/actions/PlayerActions';
import routes from 'rebook/mybook/routes';
import Error404 from 'rebook/apps/base/components/Error404';
import BookTrialPlayerAlert from 'rebook/apps/books/components/BookTrialPlayerAlert';
import BookTrialPlayerAlertNonRent from 'rebook/apps/books/components/BookTrialPlayerAlert/nonRent';

import PromoCampaignBanner from './PromoCampaignBanner';
import Header from './Header';
import Footer from './Footer';
import PopupDialogBox from './PopupDialogBox';
import Player from './Player';

import styles from './style.scss';


class ErrorBoundary extends React.Component {
  // Intercepts errors located in child components and sends them to the sentry
  componentDidCatch(error, errorInfo) {
    Raven.captureException(error, { extra: errorInfo });
  }

  render() {
    return this.props.children;
  }
}

export class App extends React.Component {
  static fetchInitialDataForServer(store) {
    return store.dispatch(AppActions.fetchInitialData());
  }

  componentDidMount() {
    const { seo, location } = this.props;
    this.props.AppActions.fetchInitialData(location);
    this.setDomTitle(seo.title);
  }

  componentDidUpdate(prevProps) {
    const { error404, Error404Actions, seo, user, player } = this.props;
    if (seo.title !== prevProps.seo.title) {
      this.setDomTitle(seo.title);
    }
    // scroll to top on location change
    // https://reacttraining.com/react-router/web/guides/scroll-restoration
    if (this.props.location.pathname !== prevProps.location.pathname) {
      window.scrollTo(0, 0);
      this.sendRouteChangeEvent();
      if (error404.isActive) {
        Error404Actions.hide();
      }
    }

    // если залогиненный пользователь с открытым плеером вышел, тогда закрываем плеер
    if (prevProps.user && !user && player.showPlayer) {
      this.hidePlayer();
    }

    // если анонимный пользователь с открытым плеером залогинился, тогда обновляем данные о книге
    if (!prevProps.user && user && player.showPlayer) {
      this.props.PlayerActions.updateBookData();
    }
  }

  setDomTitle(title) {
    // change document title client side
    document.title = title;
  }

  sendRouteChangeEvent() {
    const page = this.props.location.search ?
      `${this.props.location.pathname}${this.props.location.search}` :
      this.props.location.pathname;

    ga('set', 'page', page);
    ga('send', 'pageview');
    yaMetrikaHit(page);
  }

  hidePlayer() {
    this.props.PlayerActions.hidePlayer();
  }

  getPlayerComponent(params) {
    const { bookId, availableForUser } = params;
    const PlayerWrapper = new Player({
      bookId,
      hidePlayer: () => this.hidePlayer(),
      isActiveSubscription: availableForUser,
    });
    return PlayerWrapper.player();
  }

  render() {
    const { error404, promoCampaign, player, user } = this.props;
    const { showPlayer, bookId, availableForUser, rentBookPaymentLink } = player;

    return (
      <ErrorBoundary>
        <div className="app">
          <Route
            path="/"
            render={() => (
              <PromoCampaignBanner promoCampaign={promoCampaign} />
            )} />
          <Route path="/" component={Header} />
          <div className="article">
            <PopupDialogBox />
            {error404.isActive
              ? <Error404 />
              : renderRoutes(routes)
            }
          </div>
          <Route path="/" component={Footer} />
          {(bookId && showPlayer) && (
            <div className={classNames(styles.playerContainer, 'jest-player-container')}>
              {!availableForUser && (
                <React.Fragment>
                  <BookTrialPlayerAlert paymentLink={rentBookPaymentLink} user={user} />
                  <BookTrialPlayerAlertNonRent paymentLink={'/trial/?from=player'} user={user} />
                </React.Fragment>
              )}
              { this.getPlayerComponent(player) }
            </div>
          )}
        </div>
      </ErrorBoundary>
    );
  }
}

function mapStateToProps(state) {
  return {
    user: state.user,
    payments: state.payments,
    bookDetail: state.bookDetail,
    player: state.player,
    seo: state.seo,
    error404: state.error404,
    booksPerSubscriptionCounter: state.booksPerSubscriptionCounter,
    promoCampaign: state.promoCampaign,
  };
}

export function mapDispatchToProps(dispatch) {
  return {
    AppActions: bindActionCreators(AppActions, dispatch),
    Error404Actions: bindActionCreators(Error404Actions, dispatch),
    PlayerActions: bindActionCreators(PlayerActions, dispatch),
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
